import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import shellStyles from '../../components/shell/shell.module.css';
import { StorePreview, type PreviewDraft } from '../../components/editor/StorePreview';
import ui from '../../styles/ui.module.css';
import ed from './editor.module.css';
import { IconDesktop, IconDown, IconEye, IconEyeOff, IconLock, IconMobile, IconUp } from './EditorIcons';

const TEMPLATE_LABEL: Record<string, string> = {
  minimalista: 'Minimalista',
  comunidade: 'Comunidade',
  oferta: 'Oferta',
};

const SECTION_LABEL: Record<string, string> = {
  feat: 'Destaque',
  grid: 'Catálogo',
  quote: 'Depoimento',
};

const ACCENT_SWATCHES = ['#1f5e4a', '#4338ca', '#b5482e', '#2563eb', '#be185d', '#0f766e', '#2dd4a0'];

const ROOT_DOMAIN = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'lvh.me:3000';
function storeUrl(subdomain: string): string {
  const scheme = ROOT_DOMAIN.includes('lvh.me') ? 'http' : 'https';
  return `${scheme}://${subdomain}.${ROOT_DOMAIN}`;
}

type Section = { key: string; visible: boolean };

/** P9 · editor de loja (M7, ADR 0018). Salva ao vivo — sem rascunho/publicar separados (o
 *  "não publicado ainda" já é `store.status`, ver ADR 0018 §2). */
export function Loja() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });

  const [template, setTemplate] = useState('minimalista');
  const [primaryColor, setPrimaryColor] = useState('#111111');
  const [accentColor, setAccentColor] = useState('#2dd4a0');
  const [tagline, setTagline] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [testimonialQuote, setTestimonialQuote] = useState('');
  const [testimonialAuthor, setTestimonialAuthor] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!store.data) return;
    setTemplate(store.data.template);
    setPrimaryColor(store.data.primaryColor);
    setAccentColor(store.data.accentColor);
    setTagline(store.data.tagline ?? '');
    setSections(store.data.sections);
    setTestimonialQuote(store.data.testimonialQuote ?? '');
    setTestimonialAuthor(store.data.testimonialAuthor ?? '');
    setLogoUrl(store.data.logoUrl);
  }, [store.data]);

  const togglePublish = useMutation({
    mutationFn: () => (store.data?.status === 'published' ? api.unpublishStore() : api.publishStore()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store'] }),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (logoFile) await api.uploadLogo(logoFile);
      await Promise.all([
        api.updateBranding({ template, primaryColor, accentColor, tagline: tagline || undefined }),
        api.updateCustomization({
          sections,
          testimonialQuote: testimonialQuote || undefined,
          testimonialAuthor: testimonialAuthor || undefined,
        }),
      ]);
    },
    onSuccess: () => {
      setSavedAt(new Date());
      setLogoFile(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ['store'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível salvar'),
  });

  function move(index: number, dir: -1 | 1) {
    setSections((cur) => {
      const next = [...cur];
      const target = index + dir;
      if (target < 0 || target >= next.length) return cur;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  function toggleSection(key: string) {
    setSections((cur) => cur.map((s) => (s.key === key ? { ...s, visible: !s.visible } : s)));
  }

  if (!store.data) {
    return (
      <div className={shellStyles.head}>
        <h1>Loja</h1>
      </div>
    );
  }

  const draft: PreviewDraft = {
    name: store.data.name,
    tagline,
    primaryColor,
    accentColor,
    logoUrl: logoFile ? URL.createObjectURL(logoFile) : logoUrl,
    sections,
    testimonialQuote,
    testimonialAuthor,
  };

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Loja</h1>
        <p>Modelo, marca e seções — o preview ao lado atualiza na hora.</p>
      </div>

      <div className={ed.bar}>
        <span className={`${ui.pill} ${store.data.status === 'published' ? ui.pillPos : ui.pillMuted}`}>
          {store.data.status === 'published' ? 'Publicada' : 'Rascunho'}
        </span>
        <a className={ui.faint} href={storeUrl(store.data.subdomain)} target="_blank" rel="noreferrer">
          {storeUrl(store.data.subdomain).replace(/^https?:\/\//, '')}
        </a>
        <span style={{ flex: 1 }} />
        {savedAt ? <span className={ed.saved}>salvo {savedAt.toLocaleTimeString('pt-BR')}</span> : null}
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={togglePublish.isPending}
          onClick={() => togglePublish.mutate()}
        >
          {store.data.status === 'published' ? 'Despublicar' : 'Publicar loja'}
        </button>
        <button className={`${ui.btn} ${ui.btnPrimary} ${ui.btnSmall}`} disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
      {error ? <p className={ui.error}>{error}</p> : null}

      <div className={ed.layout}>
        <div className={ed.panel}>
          <details className={ed.grp} open>
            <summary>Modelo</summary>
            <div className={ed.grpBody}>
              <div className={ed.tpls}>
                {Object.entries(TEMPLATE_LABEL).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    className={`${ed.tpl} ${template === v ? ed.tplActive : ''}`}
                    onClick={() => setTemplate(v)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </details>

          <details className={ed.grp} open>
            <summary>Marca</summary>
            <div className={ed.grpBody}>
              <p className={ed.lbl}>Logo</p>
              <div className={ed.logoRow}>
                {draft.logoUrl ? (
                  <img className={ed.logoPreview} src={draft.logoUrl} alt="" />
                ) : (
                  <span className={ed.logoPreview} />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                />
              </div>

              <p className={ed.lbl}>Cor principal</p>
              <input type="color" className={ed.colorInput} value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />

              <p className={ed.lbl}>Cor de destaque</p>
              <div className={ed.sw}>
                {ACCENT_SWATCHES.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    className={`${ed.swBtn} ${accentColor.toLowerCase() === hex ? ed.swActive : ''}`}
                    style={{ background: hex }}
                    aria-label={hex}
                    onClick={() => setAccentColor(hex)}
                  />
                ))}
                <input type="color" className={ed.colorInput} value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
              </div>

              <p className={ed.lbl}>Frase de efeito</p>
              <label className={ui.field} style={{ fontWeight: 400 }}>
                <input value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={140} />
              </label>
            </div>
          </details>

          <details className={ed.grp} open>
            <summary>Seções</summary>
            <div className={ed.grpBody}>
              <p className={ed.lbl}>Toque no olho pra ocultar · setas pra reordenar</p>
              <div className={ed.secs}>
                <div className={`${ed.sec} ${ed.secLocked}`}>
                  <span className={ed.secName}>Cabeçalho</span>
                  <IconLock />
                </div>

                {sections.map((sec, i) => (
                  <div key={sec.key} className={`${ed.sec} ${sec.visible ? '' : ed.secOff}`}>
                    <button type="button" className={ed.secBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Mover para cima">
                      <IconUp />
                    </button>
                    <button
                      type="button"
                      className={ed.secBtn}
                      disabled={i === sections.length - 1}
                      onClick={() => move(i, 1)}
                      aria-label="Mover para baixo"
                    >
                      <IconDown />
                    </button>
                    <span className={ed.secName}>{SECTION_LABEL[sec.key] ?? sec.key}</span>
                    <button
                      type="button"
                      className={`${ed.secBtn} ${sec.visible ? ed.secEyeOn : ''}`}
                      onClick={() => toggleSection(sec.key)}
                      aria-label={sec.visible ? 'Ocultar seção' : 'Mostrar seção'}
                    >
                      {sec.visible ? <IconEye /> : <IconEyeOff />}
                    </button>
                  </div>
                ))}

                <div className={`${ed.sec} ${ed.secLocked}`}>
                  <span className={ed.secName}>Rodapé</span>
                  <IconLock />
                </div>
              </div>

              {sections.some((s) => s.key === 'quote' && s.visible) ? (
                <>
                  <p className={ed.lbl}>Depoimento</p>
                  <label className={ui.field} style={{ fontWeight: 400 }}>
                    <textarea
                      rows={2}
                      placeholder="“Mudou minha vida…”"
                      value={testimonialQuote}
                      onChange={(e) => setTestimonialQuote(e.target.value)}
                    />
                  </label>
                  <label className={ui.field} style={{ fontWeight: 400 }}>
                    <input
                      placeholder="Autor — ex.: Rafael M."
                      value={testimonialAuthor}
                      onChange={(e) => setTestimonialAuthor(e.target.value)}
                    />
                  </label>
                </>
              ) : null}
            </div>
          </details>
        </div>

        <div className={ed.stage}>
          <div className={ed.devbar}>
            <button
              type="button"
              className={`${ed.devbtn} ${device === 'desktop' ? ed.devActive : ''}`}
              onClick={() => setDevice('desktop')}
            >
              <IconDesktop /> Computador
            </button>
            <button
              type="button"
              className={`${ed.devbtn} ${device === 'mobile' ? ed.devActive : ''}`}
              onClick={() => setDevice('mobile')}
            >
              <IconMobile /> Celular
            </button>
          </div>

          <div className={`${ed.win} ${device === 'mobile' ? ed.winMobile : ''}`}>
            <div className={ed.winBar}>
              <span className={ed.winDot} />
              <span className={ed.winDot} />
              <span className={ed.winDot} />
              <span className={ed.winUrl}>{storeUrl(store.data.subdomain).replace(/^https?:\/\//, '')}</span>
            </div>
            <StorePreview draft={draft} products={products.data ?? []} device={device} />
          </div>
        </div>
      </div>
    </>
  );
}
